// Graceful recovery from chunk-load failures after a release.
//
// After a deploy the hashed chunk files of the previous build are removed from
// the origin. A client still running the old HTML (open tab / bfcache) then
// requests a chunk that no longer exists -> 404 -> broken/blank content.
// Here we catch that failure and do a guarded full reload so the browser
// fetches fresh HTML that references the current (existing) chunk hashes.
//
// Nuxt's own chunk-reload plugin (`experimental.emitRouteChunkError`, default
// `automatic`) already covers chunk errors raised while *navigating*: it turns
// `vite:preloadError` into `app:chunkError` and reloads through the router.
// What it does not see, and what this plugin adds:
//   - a chunk <script>/<link> that 404s outside navigation (async component or
//     prefetch failing during render),
//   - a chunk error surfacing as an unhandled promise rejection.
//
// Mitigation only. The root fix is chunk retention on origin + HTML cache
// headers (see CAN-819 / CAN-820).

const RELOAD_COOLDOWN_MS = 10000;
const NUXT_CHUNK_RE = /\/_nuxt\/[^/]+\.(?:js|mjs|css)(?:\?|$)/;

function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const message = error.message || '';
  // vite: "Failed to fetch dynamically imported module: <url>",
  // "Unable to preload CSS for <url>";
  // safari: "Importing a module script failed"
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(
    message,
  );
}

// A failed chunk <script>/<link> fires an 'error' event that does NOT bubble,
// so it must be caught in the capture phase at window. This is the path that
// catches async-component / prefetch chunk failures happening during render,
// which never reach the router (Vue consumes the async-component promise
// rejection internally).
function isChunkResourceError(event) {
  const el = event && event.target;
  if (!el || !el.tagName) return false;
  const url =
    (el.tagName === 'SCRIPT' && el.src) ||
    (el.tagName === 'LINK' && el.href) ||
    '';
  return NUXT_CHUNK_RE.test(url);
}

// `reloadNuxtApp` keeps a `nuxt:reload` marker in sessionStorage and skips a
// repeat reload of the same path within `ttl`, so a genuinely broken deploy
// (fresh HTML also failing) does not cause an infinite reload loop.
function reloadOnce() {
  reloadNuxtApp({ ttl: RELOAD_COOLDOWN_MS });
}

export default defineNuxtPlugin(() => {
  window.addEventListener(
    'error',
    (event) => {
      if (isChunkResourceError(event)) reloadOnce();
    },
    true,
  );

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) reloadOnce();
  });
});
